import {
  AlertDialog,
  Button,
  Checkbox,
  Label,
  Tooltip,
  Typography,
  useOverlayState,
} from "@heroui/react";
import {ShuffleIcon} from "@project/icons-web";
import {useEffect, useRef, useState} from "react";

import {useThemeBuilder} from "./theme-builder-context";

const SHUFFLE_WARNING_KEY = "theme-builder:shuffle-warning-shown";

/* ------------------------------------------------------------------------------------------------
 * ShuffleButton
 * Randomizes theme params. Shows an AlertDialog confirmation the first time (unless localStorage
 * flag is set, or the params haven't changed from the last snapshot).
 * ------------------------------------------------------------------------------------------------ */
export function ShuffleButton() {
  const {params, randomize} = useThemeBuilder();
  const [isDontShowAgainChecked, setIsDontShowAgainChecked] = useState(false);
  const modalState = useOverlayState();

  // Snapshot is updated after each randomize so that immediately re-randomizing skips the dialog
  const snapshotRef = useRef(params);
  const isRandomizingRef = useRef(false);

  const handleRandomize = () => {
    isRandomizingRef.current = true;
    randomize();
    if (isDontShowAgainChecked) {
      localStorage.setItem(SHUFFLE_WARNING_KEY, "true");
    }
    modalState.close();
  };

  const handleModalTrigger = () => {
    const dontShowAgain = localStorage.getItem(SHUFFLE_WARNING_KEY) === "true";
    const isSameAsSnapshot = JSON.stringify(params) === JSON.stringify(snapshotRef.current);

    if (dontShowAgain || isSameAsSnapshot) {
      isRandomizingRef.current = true;

      return randomize();
    }

    modalState.open();
  };

  const handleOpenChange = (isOpen: boolean) => {
    setIsDontShowAgainChecked(false);
    modalState.setOpen(isOpen);
  };

  const handleClose = () => {
    setIsDontShowAgainChecked(false);
    modalState.close();
  };

  // After randomize fires, update snapshot so the next press skips the dialog
  useEffect(() => {
    if (isRandomizingRef.current) {
      snapshotRef.current = params;
      isRandomizingRef.current = false;
    }
  }, [params]);

  return (
    <div className="flex flex-col gap-1">
      <Tooltip delay={0} closeDelay={0}>
        <Tooltip.Trigger className="w-min">
          <Button isIconOnly size="md" variant="tertiary" onPress={handleModalTrigger}>
            <ShuffleIcon />
          </Button>
        </Tooltip.Trigger>
        <Tooltip.Content>
          <Tooltip.Arrow />
          <Typography>Randomize</Typography>
        </Tooltip.Content>
      </Tooltip>

      <AlertDialog.Backdrop
        isDismissable
        isOpen={modalState.isOpen}
        onOpenChange={handleOpenChange}
      >
        <AlertDialog.Container>
          <AlertDialog.Dialog>
            <AlertDialog.CloseTrigger />
            <AlertDialog.Header>
              <AlertDialog.Icon status="default">
                <ShuffleIcon />
              </AlertDialog.Icon>
              <AlertDialog.Heading>Are you sure you want to randomize?</AlertDialog.Heading>
            </AlertDialog.Header>
            <AlertDialog.Body>This will overwrite your current theme settings.</AlertDialog.Body>
            <AlertDialog.Footer className="flex-col sm:flex-row sm:items-center">
              <Button
                className="order-2 w-full sm:order-2 sm:w-auto"
                size="md"
                variant="tertiary"
                onPress={handleClose}
              >
                Cancel
              </Button>
              <Button
                className="order-1 w-full sm:order-3 sm:w-auto"
                size="md"
                onPress={handleRandomize}
              >
                Confirm
              </Button>
              <div className="order-3 flex flex-1 items-center gap-2 self-start sm:order-1 sm:self-center">
                <Checkbox
                  id="shuffle-dont-show-again"
                  isSelected={isDontShowAgainChecked}
                  onChange={setIsDontShowAgainChecked}
                >
                  <Checkbox.Control>
                    <Checkbox.Indicator />
                  </Checkbox.Control>
                </Checkbox>
                <Label htmlFor="shuffle-dont-show-again">Don't show again</Label>
              </div>
            </AlertDialog.Footer>
          </AlertDialog.Dialog>
        </AlertDialog.Container>
      </AlertDialog.Backdrop>
    </div>
  );
}
